import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { History } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { CardContent, CardFooter, CardIcon } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ListCard } from './ListCard'
import { ListCardHeader } from './ListCardHeader'
import { AuditTimeline } from './AuditTimeline'
import { AuditHistorySheet } from './AuditHistorySheet'
import { AuditHistoryApi, type AuditObjectType } from '@/services/shared/auditHistoryApi'
import { AuthService } from '@/services/core/auth'

export interface AuditHistoryCardProps {
  objectType: AuditObjectType
  objectId: string
  /** Name of the object, shown as the sheet's subtitle. */
  objectName?: string
  /** How many of the most recent changes the card lists. */
  limit?: number
  /** Shown above the changes, such as the object's notes. */
  children?: ReactNode
}

/**
 * The last few changes made to an object, newest first. The card is a summary:
 * each entry shows only its first couple of messages, and the whole history
 * lives in the sheet behind "view all". Anything passed as children, such as
 * the object's notes, sits above the changes.
 */
export function AuditHistoryCard({
  objectType,
  objectId,
  objectName,
  limit = 5,
  children,
}: AuditHistoryCardProps) {
  const { t } = useTranslation()
  const [sheetOpen, setSheetOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['audithistory', objectType, objectId, 1, limit],
    queryFn: () => AuditHistoryApi.getPage(objectType, objectId, { page: 1, pageSize: limit }),
    enabled: AuthService.isAuthenticated() && !!objectId,
  })

  const entries = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  return (
    <ListCard tone="insight">
      <ListCardHeader
        title={
          <>
            <CardIcon icon={History} />
            {t('audit.latestActivity')}
          </>
        }
        count={totalCount}
      />
      <CardContent>
        {children && (
          <>
            {children}
            <Separator className="my-4" />
          </>
        )}
        {isLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
        {!isLoading && entries.length === 0 && (
          <p className="text-sm text-(--ad-text-muted)">{t('audit.empty')}</p>
        )}
        {!isLoading && entries.length > 0 && (
          <AuditTimeline entries={entries} maxMessagesPerEntry={2} />
        )}
      </CardContent>
      {totalCount > 0 && (
        <CardFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => setSheetOpen(true)}>
            {t('audit.viewAll')}
          </Button>
        </CardFooter>
      )}

      <AuditHistorySheet
        objectType={objectType}
        objectId={objectId}
        objectName={objectName}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </ListCard>
  )
}
